/**
 * Icon — 목업(HTML 시안) 인라인 SVG 아이콘 세트 1:1 이식.
 *
 * 목업의 `<Icon name="doc" size={18}/>` 와 동일 API. 외부 아이콘 패키지 비의존
 * (기존 AppShell NavIcon 과 같은 인라인 path 방식). 모듈 분기 없는 범용 프레젠테이션.
 * path 문자열은 공백 " M" 으로 다중 서브패스를 분리한다(목업 동일 규칙).
 */
import type { CSSProperties } from "react";

import { readKey } from "@/lib/safeAccess";

/** 아이콘 이름 → SVG path d (HTML 시안 ICONS 원본 그대로) */
const ICONS: Record<string, string> = {
  dashboard: "M3 3h7v8H3zM14 3h7v5h-7zM14 11h7v10h-7zM3 14h7v7H3z",
  doc: "M14 3v4a1 1 0 0 0 1 1h4 M5 3h9l5 5v12a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z M8 13h8 M8 17h6",
  capa: "M9 11l2.5 2.5L16 9 M5 4h14a1 1 0 0 1 1 1v15l-4-2-4 2-4-2-4 2V5a1 1 0 0 1 1-1z",
  nc: "M12 9v4 M12 17h.01 M10.3 4.3 2.5 18a2 2 0 0 0 1.7 3h15.6a2 2 0 0 0 1.7-3L13.7 4.3a2 2 0 0 0-3.4 0z",
  change: "M3 7h11 M3 7l3.5-3.5M3 7l3.5 3.5 M21 17H10 M21 17l-3.5-3.5M21 17l-3.5 3.5",
  risk: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z M9.5 12l1.8 1.8 3.5-3.6",
  supplier: "M3 9l1-5h16l1 5 M4 9v11h16V9 M4 9h16 M9 13h6",
  audit: "M11 6a5 5 0 1 0 0 10 5 5 0 0 0 0-10z M15 14l5 5 M9 11h4 M11 9v4",
  training: "M22 9 12 5 2 9l10 4 10-4z M6 11v5c0 1 2.7 3 6 3s6-2 6-3v-5 M22 9v5",
  design:
    "M12 2v3 M12 2a6 6 0 0 0-3 11.2V18h6v-4.8A6 6 0 0 0 12 2z M9 18v1a3 3 0 0 0 6 0v-1 M5 8H2M22 8h-3",
  search: "M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14z M20 20l-3.5-3.5",
  bell: "M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9 M13.7 21a2 2 0 0 1-3.4 0",
  plus: "M12 5v14 M5 12h14",
  chevR: "M9 6l6 6-6 6",
  chevD: "M6 9l6 6 6-6",
  chevU: "M6 15l6-6 6 6",
  chevL: "M15 6l-6 6 6 6",
  check: "M5 12l5 5L20 7",
  x: "M6 6l12 12 M18 6 6 18",
  clock: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z M12 7v5l3 2",
  up: "M7 17 17 7 M9 7h8v8",
  down: "M17 7 7 17 M7 9v8h8",
  lock: "M5 11h14v10H5z M8 11V7a4 4 0 0 1 8 0v4 M12 15v3",
  pen: "M4 20h4l10.5-10.5a2.1 2.1 0 0 0-3-3L5 17v3z M13.5 6.5l3 3",
  download: "M12 3v12 M7 11l5 4 5-4 M5 21h14",
  filter: "M3 5h18l-7 8v6l-4 2v-8L3 5z",
  more: "M5 12h.01M12 12h.01M19 12h.01",
  grip: "M9 5h.01 M9 12h.01 M9 19h.01 M15 5h.01 M15 12h.01 M15 19h.01",
  user: "M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M4 21c0-4 4-6 8-6s8 2 8 6",
  cal: "M5 4h14a1 1 0 0 1 1 1v15H4V5a1 1 0 0 1 1-1z M4 9h16 M8 3v4M16 3v4",
  arrowUR: "M7 17 17 7 M8 7h9v9",
  cornerDR: "M4 4v8a3 3 0 0 0 3 3h13 M17 12l3 3-3 3",
  eye: "M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7z M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z",
  eyeOff:
    "M3 3l18 18 M10.6 10.6a3 3 0 0 0 4 4 M9.9 5.1A9.5 9.5 0 0 1 12 5c6 0 10 7 10 7a16 16 0 0 1-3.1 3.6 M6.1 6.1A16 16 0 0 0 2 12s4 7 10 7a9.5 9.5 0 0 0 3.3-.6",
  link: "M9 15l6-6 M10 6l1-1a4 4 0 0 1 6 6l-1 1 M14 18l-1 1a4 4 0 0 1-6-6l1-1",
  sparkle: "M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3z",
  shieldCheck: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z M9 12l2 2 4-4",
  flag: "M5 21V4 M5 4h11l-2 4 2 4H5",
  info: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z M12 11v5 M12 8h.01",
  mail: "M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z M3 7l9 6 9-6",
  history: "M3 12a9 9 0 1 0 3-6.7L3 8 M3 4v4h4 M12 8v4l3 2",
  trend: "M3 17l6-6 4 4 8-8 M21 7v5h-5",
  trendDown: "M3 7l6 6 4-4 8 8 M21 17v-5h-5",
  layers: "M12 3 3 8l9 5 9-5-9-5z M3 13l9 5 9-5 M3 17l9 5 9-5",
  target: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8z M12 12h.01",
  settings:
    "M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z M19.4 13.5a7.9 7.9 0 0 0 0-3l2-1.5-2-3.5-2.4 1a8 8 0 0 0-2.6-1.5L14 2h-4l-.4 2.5a8 8 0 0 0-2.6 1.5l-2.4-1-2 3.5 2 1.5a7.9 7.9 0 0 0 0 3l-2 1.5 2 3.5 2.4-1a8 8 0 0 0 2.6 1.5L10 22h4l.4-2.5a8 8 0 0 0 2.6-1.5l2.4 1 2-3.5-2-1.5z",
  inbox: "M3 13h5l1 3h6l1-3h5 M3 13l3-8h12l3 8v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-6z",
  box: "M21 8 12 3 3 8v8l9 5 9-5V8z M3 8l9 5 9-5 M12 13v9",
  grid: "M3 3h7v7H3z M14 3h7v7h-7z M14 14h7v7h-7z M3 14h7v7H3z",
  minus: "M5 12h14",
  folder:
    "M3 7a2 2 0 0 1 2-2h3.6a2 2 0 0 1 1.4.6L11.8 7H19a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z",
  folderOpen:
    "M3 8V6a1 1 0 0 1 1-1h4l2 2h7a1 1 0 0 1 1 1v1 M3 8h17l-1.7 8.5a1 1 0 0 1-1 .8H5.5a1 1 0 0 1-1-.8z",
  logout: "M9 21H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h4 M16 17l5-5-5-5 M21 12H9",
  complaint: "M5 4h14a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H9l-4 4V5a1 1 0 0 1 1-1z M12 7v4 M12 13h.01",
  globe:
    "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z M3.5 9h17 M3.5 15h17 M12 3c2.6 2.5 2.6 15.5 0 18 M12 3c-2.6 2.5-2.6 15.5 0 18",
  clipboard:
    "M8 5H6a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1h-2 M8 4h8v3H8z M9 12h6 M9 16h4",
  pulse: "M3 12h4l2 6 4-14 2 8h6",
  dot: "M12 12m-3 0a3 3 0 1 0 6 0 3 3 0 1 0-6 0",
  expand:
    "M8 3H5a2 2 0 0 0-2 2v3 M16 3h3a2 2 0 0 1 2 2v3 M21 16v3a2 2 0 0 1-2 2h-3 M8 21H5a2 2 0 0 1-2-2v-3",
  compress:
    "M8 3v3a2 2 0 0 1-2 2H3 M16 3v3a2 2 0 0 0 2 2h3 M21 16h-3a2 2 0 0 0-2 2v3 M3 16h3a2 2 0 0 1 2 2v3",
  trash:
    "M4 7h16 M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2 M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13",
  project:
    "M4 5a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z M10 4v16 M16 4v16",
  beaker:
    "M9 3h6 M10 3v5.5L4.6 18a1.5 1.5 0 0 0 1.3 2.3h12.2a1.5 1.5 0 0 0 1.3-2.3L14 8.5V3 M7.5 14h9",
  bug: "M9 8a3 3 0 0 1 6 0 M5 12h14 M12 11v9 M5 15H3 M5 19H3 M21 15h-2 M21 19h-2 M6 12v3a6 6 0 0 0 12 0v-3",
  gitBranch:
    "M6 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4z M6 7v10 M6 21a2 2 0 1 0 0-4 2 2 0 0 0 0 4z M18 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4z M18 7a6 6 0 0 1-6 6H8",
  list: "M8 6h12 M8 12h12 M8 18h12 M4 6h.01 M4 12h.01 M4 18h.01",
  award: "M12 3a5 5 0 1 0 0 10 5 5 0 0 0 0-10z M8.5 12.5L7 21l5-2.5L17 21l-1.5-8.5",
  cpu: "M6 6h12v12H6z M9 9h6v6H9z M9 2v2 M15 2v2 M9 20v2 M15 20v2 M2 9h2 M2 15h2 M20 9h2 M20 15h2",
};

interface IconProps {
  name: string;
  size?: number;
  style?: CSSProperties;
  strokeWidth?: number;
  className?: string;
}

/** 인라인 SVG 아이콘 (목업 Icon 1:1). 미정의 이름은 dot 으로 폴백. */
export function Icon({ name, size = 18, style, strokeWidth = 1.8, className }: IconProps) {
  const d = readKey(ICONS, name) ?? ICONS.dot;
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
      className={className}
      aria-hidden
    >
      {d.split(" M").map((seg, i) => (
        <path key={i} d={i ? `M${seg}` : seg} />
      ))}
    </svg>
  );
}
